import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { Layout } from "antd";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getRdxSelectionMapper, getRdxActionMapper } from "rdx/utils/propsMapping";
import SessionClient from "util/SessionClient";
import HeaderLogoLarge from "components/Icons/Logos/HeaderLogoLarge";
import EnvelopeOutlineIcon from "components/Icons/EnvelopeOutlineIcon";
import messages from "rdx/modules/emailVerification/messages";

import styles from "./styles";

type VerifyEmailStatusT = "idle" | "pending" | "success" | "expired" | "invalid" | "stripe_error";

type PropsT = {
  verifyEmailStatus: VerifyEmailStatusT;
  verifyStripeError: string | null;
  verifyEmailRequest: (payload: { token: string }) => void;
  setVerifyEmailStatus: (status: VerifyEmailStatusT) => void;
  setAlertMessageVisible: (payload: { message: string; severity: string }) => void;
};

const ctaButtonSx = {
  background: "linear-gradient(90deg, #f97316, #fb923c)",
  borderRadius: "50px",
  py: 1.75,
  px: 4,
  minWidth: 220,
  fontSize: "1rem",
  fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
  textTransform: "none",
  boxShadow: "0 4px 24px rgba(249,115,22,0.35)",
  mt: 1,
  "&:hover": { background: "linear-gradient(90deg, #ea6c0a, #f97316)" },
} as const;

const VerifyEmail = ({
  verifyEmailStatus,
  verifyStripeError,
  verifyEmailRequest,
  setVerifyEmailStatus,
  setAlertMessageVisible,
}: PropsT) => {
  const { token } = useParams<{ token?: string }>();
  const history = useHistory();
  const session = new SessionClient();

  useEffectOnce(() => {
    if (!token) {
      history.replace("/login");
      return;
    }
    setVerifyEmailStatus("pending");
    verifyEmailRequest({ token });
  });

  useEffect(() => {
    if (verifyEmailStatus === "success") {
      setAlertMessageVisible({
        message: messages.VERIFY_EMAIL_SUCCESS,
        severity: "success",
      });
    }
  }, [verifyEmailStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuccessRedirect = async () => {
    if (session.jwt && session.user) {
      try {
        await session.user.reload("verify-email");
      } catch (_e) {
        // fall through; router guard will re-evaluate from whatever session state we have
      }
      history.push("/dashboard");
    } else {
      history.push("/login");
    }
  };

  const renderBody = () => {
    if (verifyEmailStatus === "success") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center" }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: "#20C2F1" }} />
          <Typography variant="h5" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "white" }}>
            {"Email "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              verified!
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: 16 }}>
            You can now sign in to your account.
          </Typography>
          <Button variant="contained" onClick={handleSuccessRedirect} sx={ctaButtonSx}>
            {session.jwt ? "Go to Dashboard" : "Sign in"}
          </Button>
        </Box>
      );
    }

    if (verifyEmailStatus === "expired") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center" }}>
          <EnvelopeOutlineIcon width={64} height={64} fill="rgba(255,255,255,0.5)" />
          <Typography variant="h5" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "white" }}>
            {"Verification link "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              expired
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: 16 }}>
            Your link is no longer valid. Sign in to request a new one.
          </Typography>
          <Button variant="contained" onClick={() => history.push("/login")} sx={ctaButtonSx}>
            Sign in
          </Button>
        </Box>
      );
    }

    if (verifyEmailStatus === "stripe_error") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center" }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "rgba(255,255,255,0.5)" }} />
          <Typography variant="h5" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "white" }}>
            {"Payment "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              declined
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: 16 }}>
            {verifyStripeError || "Your card was declined. Please update your payment method."}
          </Typography>
          <Button variant="contained" onClick={() => history.push("/account/payment-info")} sx={ctaButtonSx}>
            Update payment method
          </Button>
        </Box>
      );
    }

    if (verifyEmailStatus === "invalid") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center" }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "rgba(255,255,255,0.5)" }} />
          <Typography variant="h5" sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: "white" }}>
            {"Verification "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #38bdf8 0%, #20C2F1 55%, #67e8f9 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              failed
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: 16 }}>
            This link is invalid or has already been used.
          </Typography>
          <Button variant="contained" onClick={() => history.push("/login")} sx={ctaButtonSx}>
            Sign in
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <CircularProgress size={48} sx={{ color: "#20C2F1" }} />
        <Typography sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: 16 }}>
          Verifying your email...
        </Typography>
      </Box>
    );
  };

  return (
    <Layout css={styles.pageLayout}>
      <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
        <HeaderLogoLarge width="146px" height="46px" />
      </Box>
      <div css={styles.confirmWindow}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "55%",
            height: "55%",
            background: "radial-gradient(ellipse at top left, rgba(56,189,248,0.14) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: "50%",
            height: "50%",
            background: "radial-gradient(ellipse at bottom right, rgba(32,194,241,0.10) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div css={styles.verifyBody}>{renderBody()}</div>
      </div>
    </Layout>
  );
};

export { VerifyEmail };

export default connect(
  getRdxSelectionMapper({
    verifyEmailStatus: "selectVerifyEmailStatus",
    verifyStripeError: "selectVerifyStripeError",
  }),
  getRdxActionMapper(["verifyEmailRequest", "setVerifyEmailStatus", "setAlertMessageVisible"]),
)(VerifyEmail);
